import { CloseCircleFilled } from '@ixigo/icons';
import Button from '@ixigo/iui/Button';
import IconButton from '@ixigo/iui/IconButton';
import Typography from '@ixigo/iui/Typography';
import clsx from 'clsx';
import { ChangeEvent } from 'react';
import placeholder from '../../images/filePlaceholder.svg';

export type FileUploaderProps = {
    label: string;
    value?: File | File[];
    onChange: (files: File | File[] | undefined) => void;
    onError: () => void;
    accept?: string;
    multiple?: boolean;
    maxFiles?: number;
    maxFileSize?: number; // in MB
    helperText?: string;
    className?: string;
    disabled?: boolean;
};

const FileUploader = ({
    label,
    value,
    onChange,
    onError,
    accept,
    multiple,
    maxFiles = 1,
    maxFileSize,
    helperText,
    className,
    disabled,
}: FileUploaderProps) => {
    // Convert value to array for easier handling
    const getFiles = () => {
        if (!value) return [];
        return Array.isArray(value) ? value : [value];
    };
    const files = getFiles();
    const canAddMore = !!files.length && files.length < maxFiles;

    const validateFile = (file: File) => {
        if (maxFileSize && file.size / (1024 * 1024) > maxFileSize) {
            onError();
            return false;
        }
        return true;
    };

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>, append = false) => {
        const newFiles = Array.from(e.target.files || []);
        if (!newFiles.length) return;

        const validFiles = newFiles.filter(validateFile);
        if (!validFiles.length) {
            e.target.value = '';
            return;
        }

        if (append) {
            const currentFiles = getFiles();
            const combined = [...currentFiles, ...validFiles].slice(0, maxFiles);
            onChange(multiple ? combined : combined[0]);
        } else {
            onChange(multiple ? validFiles.slice(0, maxFiles) : validFiles[0]);
        }
        e.target.value = '';
    };

    const removeFile = (index: number) => {
        if (Array.isArray(value)) {
            const newFiles = [...value];
            newFiles.splice(index, 1);
            onChange(newFiles.length ? newFiles : undefined);
        } else {
            onChange(undefined);
        }
    };

    const renderFileItem = (file: File, index: number) => {
        const isImg = file.type.includes('image');
        const uniqueKey = `${file.name}-${file.size}-${file.lastModified}`;

        return (
            <div
                key={uniqueKey}
                className='flex gap-10 items-center justify-between px-15 py-10 border border-neutral-100 rounded-10 overflow-hidden'
            >
                <div className='flex items-center gap-10 overflow-hidden'>
                    <img
                        src={isImg ? URL.createObjectURL(file) : placeholder}
                        className={clsx('w-9 h-9 rounded-5 object-cover', isImg && 'border border-neutral-100')}
                        alt={file.name}
                    />
                    <Typography className='truncate'>{file.name}</Typography>
                </div>
                <IconButton
                    color='neutral'
                    size='sm'
                    variant='flat'
                    onClick={() => removeFile(index)}
                    disabled={disabled}
                >
                    <CloseCircleFilled className='text-neutral-600' />
                </IconButton>
            </div>
        );
    };

    return (
        <div className={clsx('flex flex-col gap-10', className)}>
            {files.map(renderFileItem)}
            {canAddMore && (
                <Button size='sm' variant='flat' className='w-fit' as='label' disabled={disabled}>
                    Add more
                    <input
                        type='file'
                        accept={accept}
                        multiple={multiple}
                        className='hidden'
                        onChange={(e) => handleFileChange(e, true)}
                        disabled={disabled}
                    />
                </Button>
            )}
            {!files.length && (
                <div className='flex gap-10 items-center justify-between mt-2 px-15 py-10 border border-neutral-100 rounded-10'>
                    <div className='flex flex-col gap-0.5'>
                        <Typography className='font-medium'>{label}</Typography>
                        {helperText && <Typography className='text-neutral-600'>{helperText}</Typography>}
                    </div>
                    <Button size='sm' variant='flat' className='!p-0 shrink-0' disabled={disabled}>
                        <label
                            htmlFor={`file-upload-${label}`}
                            className='flex min-h-[30px] button-sm py-[3px] px-5 items-center'
                        >
                            <input
                                id={`file-upload-${label}`}
                                type='file'
                                accept={accept}
                                multiple={multiple}
                                className='hidden'
                                onChange={(e) => handleFileChange(e)}
                                disabled={disabled}
                            />
                            Upload
                        </label>
                    </Button>
                </div>
            )}
        </div>
    );
};

export default FileUploader;
